/* eslint-disable no-param-reassign */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  FormErrorsState,
  FormMetadataErrors,
  SetFormErrorPayload,
  ValidateFieldPayload,
  ValidateFormPayload,
} from '../types/builderTypes';

import { FORM_PROPERTIES } from '../../../../shared/constants';
import {
  // Uncomment when workflow is implemented
  // validateApprovalWorkflow,
  validateDescription,
  validateFormAction,
  validateFormName,
  validateTargetEntity,
  validateUrlSlug,
} from '../../data-layer/validation/formMetadataValidators';

const initialState: FormErrorsState = {
  errors: {},
  hasErrors: false,
};

const formErrorsSlice = createSlice({
  name: 'builder/errors',
  initialState,
  reducers: {
    setFormError: (state, action: PayloadAction<SetFormErrorPayload>) => {
      const { field, error } = action.payload;

      state.errors[field as keyof FormMetadataErrors] = error;
      state.hasErrors = Object.keys(state.errors).length > 0;
    },

    clearFormError: (state, action: PayloadAction<string>) => {
      const field = action.payload as keyof FormMetadataErrors;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { [field]: _, ...newErrors } = state.errors;

      state.errors = newErrors;
      state.hasErrors = Object.keys(newErrors).length > 0;
    },

    clearAllFormErrors: (state) => {
      state.errors = {};
      state.hasErrors = false;
    },

    setMultipleFormErrors: (
      state,
      action: PayloadAction<FormMetadataErrors>
    ) => {
      state.errors = { ...state.errors, ...action.payload };
      state.hasErrors = Object.keys(state.errors).length > 0;
    },

    validateField: (state, action: PayloadAction<ValidateFieldPayload>) => {
      const { fieldName, value, additionalContext } = action.payload;
      let error: string | null = null;

      switch (fieldName) {
        case FORM_PROPERTIES.FORM_NAME:
          error = validateFormName(value);
          break;
        case FORM_PROPERTIES.DESCRIPTION:
          error = validateDescription(value);
          break;
        case FORM_PROPERTIES.URL_SLUG:
          error = validateUrlSlug(value);
          break;
        case FORM_PROPERTIES.TARGET_ENTITY:
          error = validateTargetEntity(value);
          break;
        case FORM_PROPERTIES.FORM_ACTION:
          error = validateFormAction(
            value,
            additionalContext?.targetEntity ?? ''
          );
          break;
        case FORM_PROPERTIES.WORKFLOW:
          // Uncomment when workflow is implemented
          // error = validateApprovalWorkflow(value);
          break;
        default:
          return;
      }

      if (error) {
        state.errors[fieldName as keyof FormMetadataErrors] = error;
      } else {
        const key = fieldName as keyof FormMetadataErrors;
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { [key]: _, ...newErrors } = state.errors;

        state.errors = newErrors;
      }

      state.hasErrors = Object.keys(state.errors).length > 0;
    },

    validateForm: (state, action: PayloadAction<ValidateFormPayload>) => {
      const formData = action.payload;
      const errors: FormMetadataErrors = {};

      const formNameError = validateFormName(formData.formName);

      if (formNameError) {
        errors.formName = formNameError;
      }

      const descriptionError = validateDescription(formData.description ?? '');

      if (descriptionError) {
        errors.description = descriptionError;
      }

      const urlSlugError = validateUrlSlug(formData.urlSlug);

      if (urlSlugError) {
        errors.urlSlug = urlSlugError;
      }

      const categoryError = formData.isOrchestrationToggleOn
        ? null
        : validateTargetEntity(formData.targetEntity);

      if (categoryError) {
        errors.targetEntity = categoryError;
      }

      const actionError = formData.isOrchestrationToggleOn
        ? null
        : validateFormAction(formData.formAction, formData.targetEntity);

      if (actionError) {
        errors.formAction = actionError;
      }

      // Uncomment when workflow is implemented
      // const workflowError = validateApprovalWorkflow(formData.approvalWorkflow);

      // if (workflowError) {
      //   errors.approvalWorkflow = workflowError;
      // }

      state.errors = errors;
      state.hasErrors = Object.keys(errors).length > 0;
    },
  },
});

export const { reducer: formErrorsReducer, actions: formErrorsActions } =
  formErrorsSlice;
